import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { apiClient } from "./client";
import type { Role } from "@/store/auth-store";

export interface LoginRequest {
  username: string;
  password: string;
}

export interface UserPublic {
  id: string;
  username: string;
  full_name: string | null;
  email: string | null;
  role: Role;
  active: boolean;
  license_number: string | null;
  /** Physical location. Null = unscoped (sees the whole site). */
  location: string | null;
  last_login_at: string | null;
  created_at: string;
  /**
   * When this account stops working. Null = never, which is every member
   * of clinical staff; assessment candidates are not employees.
   *
   * The API has always returned it — the type simply never declared it,
   * so an admin could SET an expiry and then had nowhere to see it.
   */
  access_expires_at: string | null;
}

export interface LoginResponse {
  access_token: string;
  token_type: "bearer";
  expires_at: string;
  user: UserPublic;
}

export interface RefreshResponse {
  access_token: string;
  token_type: "bearer";
  expires_at: string;
}

export async function login(body: LoginRequest): Promise<LoginResponse> {
  const { data } = await apiClient.post<LoginResponse>("/auth/login", body);
  return data;
}

export async function refresh(): Promise<RefreshResponse> {
  const { data } = await apiClient.post<RefreshResponse>("/auth/refresh");
  return data;
}

export async function logout(): Promise<void> {
  await apiClient.post("/auth/logout");
}

export async function me(): Promise<UserPublic> {
  const { data } = await apiClient.get<UserPublic>("/auth/me");
  return data;
}

export interface ChangePasswordRequest {
  current_password: string;
  new_password: string;
}

export async function changeOwnPassword(body: ChangePasswordRequest): Promise<void> {
  await apiClient.put("/auth/me/password", body);
}

export interface PushAllowedResponse {
  allowed: boolean;
}

export async function getPushAllowed(): Promise<PushAllowedResponse> {
  const { data } = await apiClient.get<PushAllowedResponse>(
    "/auth/me/push-allowed",
  );
  return data;
}

export const PUSH_ALLOWED_KEY = ["auth", "push-allowed"] as const;

export function usePushAllowed(): UseQueryResult<PushAllowedResponse> {
  return useQuery({
    queryKey: PUSH_ALLOWED_KEY,
    queryFn: getPushAllowed,
    // Settings can flip mid-session, but not so often that we need to
    // refetch on every render — 60 s is a reasonable compromise.
    staleTime: 60_000,
  });
}

export interface SignAllowedResponse {
  allowed: boolean;
}

export async function getSignAllowed(): Promise<SignAllowedResponse> {
  const { data } = await apiClient.get<SignAllowedResponse>(
    "/auth/me/sign-allowed",
  );
  return data;
}

export const SIGN_ALLOWED_KEY = ["auth", "sign-allowed"] as const;

/**
 * Whether this user may draft + sign a report.
 *
 * Needed because ``usePermissions`` is a hardcoded mirror of the STATIC
 * backend role matrix and so cannot see the DB-backed
 * ``Organisation.allow_sonographer_sign`` grant. Radiologists /
 * super-admins come back true from the static matrix anyway; this only
 * changes the answer for sonographers. Same rationale as
 * ``usePushAllowed``.
 */
export function useSignAllowed(): UseQueryResult<SignAllowedResponse> {
  return useQuery({
    queryKey: SIGN_ALLOWED_KEY,
    queryFn: getSignAllowed,
    staleTime: 60_000,
  });
}
